<template>
  <div class="chat-container">
    <!-- Header -->
    <div class="chat-header">
      <div class="header-info">
        <v-avatar size="40" class="patient-avatar">
          <v-icon color="white">mdi-account</v-icon>
        </v-avatar>
        <div class="header-text">
          <h3 class="header-title">Chat com o Paciente</h3>
          <!--
            salvia-803: o telefone só existia no painel de INICIAR a conversa,
            dentro de `v-if="!chatInitiated"`. Com o chat já aberto ele sumia da
            tela inteira — e é justamente aí que o médico precisa dele, pra saber
            para qual número está falando e poder ligar se o WhatsApp não andar.
          -->
          <span class="header-subtitle">
            WhatsApp Integrado
            <template v-if="telefoneExibido">
              · {{ telefoneExibido }}
            </template>
            <template v-else-if="loadingHistory"> · carregando…</template>
            <template v-else> · sem telefone cadastrado</template>
          </span>
        </div>
      </div>
      <div class="header-actions">
        <!-- Botão para reabrir o tour desta aba -->
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              small
              color="primary"
              v-bind="attrs"
              v-on="on"
              aria-label="Ver o tour desta aba"
              class="mr-1"
              @click="startTour"
            >
              <v-icon>mdi-help-circle-outline</v-icon>
            </v-btn>
          </template>
          <span>Ver o tour desta aba</span>
        </v-tooltip>
        <v-btn
          v-if="chatInitiated && activeTab === 'chat'"
          small
          text
          color="primary"
          class="restart-btn mr-1"
          :loading="isTyping"
          @click="restartChat"
        >
          <v-icon left small>mdi-refresh</v-icon>
          Reiniciar conversa
        </v-btn>
        <v-btn
          v-if="activeTab !== 'chat'"
          icon
          small
          class="mr-1"
          :loading="loadingHistories"
          @click="refreshActiveAnamnese"
          title="Atualizar respostas"
        >
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
        <v-btn icon @click="$emit('close')" class="close-btn">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>
    </div>

    <!-- Tabs: Conversa + uma aba por anamnese -->
    <v-tabs
      v-model="tabIndex"
      show-arrows
      height="44"
      slider-color="primary"
      class="chat-tabs"
    >
      <v-tab class="chat-tab">
        <v-icon left small>mdi-whatsapp</v-icon>
        Conversa
      </v-tab>
      <v-tab
        v-for="a in anamneses"
        :key="a.id"
        class="chat-tab"
      >
        <v-icon left small>mdi-clipboard-text-outline</v-icon>
        {{ anamneseLabel(a) }}
        <v-chip x-small class="ml-2" color="primary" outlined>
          {{ a.medical_histories_count }}
        </v-chip>
      </v-tab>
    </v-tabs>

    <!-- Messages Area -->
    <div
      v-if="activeTab === 'chat'"
      class="messages-wrapper"
      ref="messagesContainer"
      data-tour="ch-mensagens"
    >
      <div v-if="!chatInitiated" class="empty-state">
        <v-icon size="64" color="grey lighten-2">mdi-message-text-outline</v-icon>
        <p>Inicie a conversa com o paciente pelo WhatsApp.</p>
        
        <div class="suggestion-chips">
          <v-text-field
            v-model="inputPhone"
            label="Número de Telefone (WhatsApp)"
            outlined
            dense
            class="mb-3"
            style="width: 100%; max-width: 300px;"
            :loading="loadingHistory"
            :disabled="loadingHistory"
            :error="semTelefone"
            :error-messages="
              semTelefone
                ? 'Paciente sem telefone. Atualize aqui ou edite o perfil.'
                : ''
            "
            append-icon="mdi-pencil"
            @click:append="redirectToEdit"
          ></v-text-field>

          <v-textarea
            v-model="initialMessage"
            label="Mensagem inicial"
            outlined
            dense
            rows="3"
            hide-details
            class="mb-3"
            style="width: 100%; max-width: 300px;"
          ></v-textarea>
          
          <v-btn
            color="primary"
            :disabled="!initialMessage.trim() || !inputPhone || isTyping"
            @click="startChat"
            :loading="isTyping"
          >
            Iniciar Conversa
          </v-btn>
        </div>
      </div>

      <template v-else>
        <template v-for="msg in messages">
          <!-- Resumo automático do sistema: centralizado, não aparece para o paciente. -->
          <div
            v-if="msg.sender === 'system'"
            :key="msg.id"
            class="message-row system-row"
          >
            <div class="message-bubble system">
              <div class="system-label">
                <v-icon small>mdi-robot-outline</v-icon> Resumo automático
              </div>
              <div class="message-content" v-html="formatMessage(msg.message)"></div>
              <div class="message-time">{{ formatTime(msg.created_at) }}</div>
            </div>
          </div>

          <div
            v-else
            :key="msg.id"
            :class="['message-row', msg.sender === 'doctor' ? 'user-row' : 'bot-row']"
          >
            <div class="message-bubble" :class="msg.sender === 'doctor' ? 'user' : 'bot'">
              <div class="message-content" v-html="formatMessage(msg.message)"></div>
              <!-- Pode exibir áudio ou mídia se tiver msg.media_url -->
              <div v-if="safeMediaUrl(msg)" class="media-preview mt-2">
                <a :href="safeMediaUrl(msg)" target="_blank" rel="noopener noreferrer" style="color: inherit; text-decoration: underline;">
                  <v-icon small>mdi-attachment</v-icon> Ver anexo
                </a>
              </div>
              <div class="message-time">{{ formatTime(msg.created_at) }}</div>

              <!--
                salvia-834: este bloco vivia DENTRO do ramo `sender === 'system'`
                (a bolha do "Resumo automático"), onde nunca podia aparecer:
                `naoEntregue()` exige `sender === 'doctor'`, e resumo nunca é. Foi
                inserido ali na salvia-810 (27/08) e ficou invisível desde entao —
                nem o aviso de "não entregue" da 810, nem a bolha âmbar da 823.
                O lugar certo é aqui, na mensagem de quem conversa.
              -->

              <!--
                salvia-810: estar no historico so prova que a mensagem SAIU. O
                Twilio aceita o envio e so depois marca `undelivered` — fora da
                janela de 24h do WhatsApp e o que sempre acontece com texto
                livre. Sem este aviso, o medico le a propria mensagem na tela e
                conclui que falou com o paciente.
              -->
              <div v-if="naoEntregue(msg)" class="message-undelivered">
                <v-icon x-small color="error">mdi-alert-circle-outline</v-icon>
                <template v-if="recusada(msg)">
                  O paciente pediu para não conversar agora — sua mensagem não
                  foi entregue.
                </template>
                <template v-else-if="preterida(msg)">
                  Não foi enviada: você escreveu outra mensagem depois, e só a
                  última foi entregue quando o paciente abriu a conversa. Reenvie
                  se ainda fizer falta.
                </template>
                <template v-else>
                  Não entregue
                  <span v-if="foraDaJanela(msg)">
                    — a janela de 24h do WhatsApp está fechada. Reabra a conversa
                    pelo convite antes de escrever.
                  </span>
                </template>
              </div>

              <!--
                Estado diferente de falha: a conversa estava fechada, o convite
                foi enviado e esta mensagem sai sozinha quando o paciente
                responder. Nada se perdeu — por isso a cor e o texto não alarmam.
              -->
              <div v-else-if="aguardandoJanela(msg)" class="message-waiting">
                <v-icon x-small color="warning">mdi-clock-outline</v-icon>
                Aguardando o paciente abrir a conversa — enviamos o convite e sua
                mensagem será entregue assim que ele responder.
              </div>
            </div>
          </div>
        </template>

        <div v-if="isTyping" class="message-row user-row">
          <div class="message-bubble user typing">
            <span class="dot"></span>
            <span class="dot"></span>
            <span class="dot"></span>
          </div>
        </div>
      </template>
    </div>

    <!-- Anamnese Q&A (uma aba por anamnese) -->
    <div v-else class="messages-wrapper anamnese-wrapper">
      <div v-if="loadingHistories && !activeHistories.length" class="empty-state">
        <v-progress-circular indeterminate color="primary" size="40" />
        <p class="mt-3">Carregando respostas da anamnese...</p>
      </div>

      <div v-else-if="!activeHistories.length" class="empty-state">
        <v-icon size="64" color="grey lighten-2">mdi-clipboard-text-outline</v-icon>
        <p>Nenhuma resposta registrada nesta anamnese ainda.</p>
      </div>

      <template v-else>
        <div
          v-for="(group, gi) in activeGroups"
          :key="gi"
          class="qa-group"
        >
          <!-- Pergunta enviada ao paciente -->
          <div class="message-row bot-row">
            <div class="message-bubble bot question-bubble">
              <div class="qa-label">Pergunta</div>
              <div class="message-content">{{ group.question }}</div>
            </div>
          </div>
          <!-- Resposta(s) do paciente -->
          <div
            v-for="ans in group.answers"
            :key="ans.id"
            class="message-row user-row"
          >
            <div class="message-bubble user answer-bubble">
              <div class="message-content">{{ ans.text }}</div>
              <div class="message-time">{{ formatTime(ans.created_at) }}</div>
            </div>
          </div>
        </div>
      </template>
    </div>

    <!-- Input Area -->
    <div class="input-area" v-if="chatInitiated && activeTab === 'chat'">
      <div class="input-wrapper">
        <textarea
          v-model="newMessage"
          placeholder="Digite sua mensagem para o paciente..."
          rows="1"
          @keydown.enter.prevent="sendMessage"
          ref="inputField"
          @input="adjustTextareaHeight"
          data-tour="ch-campo"
        ></textarea>
        <v-btn
          icon
          color="primary"
          :disabled="!newMessage.trim() || isTyping"
          @click="sendMessage"
          class="send-btn"
          data-tour="ch-enviar"
        >
          <v-icon>mdi-send</v-icon>
        </v-btn>
      </div>
    </div>

    <!-- Snackbar for notifications -->
    <v-snackbar
      v-model="snackbar.show"
      :timeout="4000"
      top
      right
      :color="snackbar.color"
      dark
      elevation="24"
    >
      <v-icon left>{{ snackbar.color === 'error' ? 'mdi-alert-circle' : 'mdi-message-badge' }}</v-icon>
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn icon v-bind="attrs" @click="snackbar.show = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import tourMixin from "@/features/tour/tourMixin";
import { formatDate, formatTime } from "@/utils/datetime";
import { escapeHtml, safeUrl } from "@/utils/sanitizeHtml";

export default {
  name: "ChatPatient",
  mixins: [tourMixin],
  data() {
    return {
      newMessage: "",
      initialMessage: "Olá! Gostaria de conversar com você sobre o seu acompanhamento.",
      inputPhone: "",
      tabIndex: 0,
      snackbar: { show: false, text: "", color: "success" }
    };
  },
  computed: {
    // salvia-803: "sem telefone" só depois de OLHAR.
    //
    // O número chega junto com o histórico do chat, e a mensagem de erro não
    // esperava a resposta: por um instante a tela afirmava que o paciente não
    // tinha telefone quando ninguém tinha verificado ainda. Dizer que não
    // existe é diferente de ainda não saber — e o médico lia a primeira coisa.
    /**
     * Telefone do paciente formatado pra leitura (salvia-803).
     *
     * Formata só o padrão brasileiro de 10/11 dígitos; qualquer outro formato
     * sai como veio, porque exibir errado é pior que exibir cru.
     */
    telefoneExibido() {
      const bruto = String(this.inputPhone || this.patientPhone || "").replace(/\D/g, "");
      if (!bruto) return "";

      const semPais = bruto.length > 11 && bruto.startsWith("55") ? bruto.slice(2) : bruto;
      if (semPais.length === 11) {
        return `(${semPais.slice(0, 2)}) ${semPais.slice(2, 7)}-${semPais.slice(7)}`;
      }
      if (semPais.length === 10) {
        return `(${semPais.slice(0, 2)}) ${semPais.slice(2, 6)}-${semPais.slice(6)}`;
      }
      return bruto;
    },
    semTelefone() {
      return !this.loadingHistory && !this.inputPhone;
    },
    tourId() {
      return "sec-chat-pg";
    },
    ...mapState("chat", [
      "messages",
      "isTyping",
      "chatInitiated",
      "patientPhone",
      "loadingHistory",
      "anamneses",
      "anamneseHistories",
      "loadingHistories"
    ]),
    patientId() {
      // Ajuste conforme o router esteja configurado. Pode ser this.$route.params.id ou vir por prop.
      return this.$route.params.id;
    },
    // tabIndex 0 = Conversa; demais índices mapeiam para a anamnese correspondente.
    activeTab() {
      if (this.tabIndex === 0) return "chat";
      const anamnese = this.anamneses[this.tabIndex - 1];
      return anamnese ? anamnese.id : "chat";
    },
    activeHistories() {
      if (this.activeTab === "chat") return [];
      return this.anamneseHistories[this.activeTab] || [];
    },
    // Agrupa respostas consecutivas pela mesma pergunta para não repetir o enunciado.
    activeGroups() {
      const groups = [];
      let current = null;
      for (const h of this.activeHistories) {
        const question =
          h.question_descricao || h.question_term || "Pergunta";
        const text =
          h.answer_term ||
          (h.info_value !== null && h.info_value !== undefined
            ? h.info_value
            : "Não informado");
        if (!current || current.questionId !== h.question_id) {
          current = { questionId: h.question_id, question, answers: [] };
          groups.push(current);
        }
        current.answers.push({
          id: h.id,
          text,
          created_at: h.created_at
        });
      }
      return groups;
    }
  },
  mounted() {
    this.focusInput();
    if (this.patientId) {
      this.fetchHistory(this.patientId);
      this.fetchAnamneses(this.patientId);
      this.listenToSoketi();
    }
  },
  beforeDestroy() {
    if (this.patientId && window.Echo) {
      window.Echo.leave(`chat.patient.${this.patientId}`);
    }
  },
  watch: {
    messages: {
      handler() {
        this.scrollToBottom();
      },
      deep: true
    },
    patientPhone: {
      handler(newPhone) {
        if (newPhone) {
          this.inputPhone = newPhone;
        }
      },
      immediate: true
    },
    activeTab(tab) {
      if (tab !== "chat" && !this.anamneseHistories[tab]) {
        this.fetchAnamneseHistories(tab);
      }
    }
  },
  methods: {
    formatDate,
    formatTime,
    ...mapActions("chat", [
      "fetchHistory",
      "startChat",
      "sendMessage",
      "receiveMessage",
      "fetchAnamneses",
      "fetchAnamneseHistories"
    ]),

    tourSteps() {
      return [
        {
          title: "Chat com o paciente",
          description:
            "Este é o canal de conversa com o paciente, integrado ao WhatsApp. Veja como usar.",
        },
        {
          anchor: "ch-mensagens",
          title: "Área de mensagens",
          description:
            "Aqui aparece todo o histórico da conversa trocada com o paciente, além dos resumos automáticos.",
        },
        {
          anchor: "ch-campo",
          title: "Escrever mensagem",
          description:
            "Digite sua mensagem neste campo. Pressione Enter para enviar rapidamente.",
        },
        {
          anchor: "ch-enviar",
          title: "Enviar",
          description:
            "Clique neste botão para enviar a mensagem ao paciente pelo WhatsApp.",
        },
      ];
    },

    anamneseLabel(a) {
      const typeMap = {
        1: "Geral",
        2: "Corpo",
        3: "Comida",
        geral: "Geral",
        nutricionista: "Nutrição",
        exames: "Exames",
        whatsapp: "WhatsApp"
      };
      const type = typeMap[a.type] || a.type || "Anamnese";
      const date = a.date ? this.formatDate(a.date) : "";
      return date ? `${type} • ${date}` : type;
    },

    refreshActiveAnamnese() {
      if (this.activeTab !== "chat") {
        this.fetchAnamneseHistories(this.activeTab);
      }
    },
    
    redirectToEdit() {
      // Ajuste o caminho se a sua rota de edição for diferente
      this.$router.push(`/pacientes/${this.patientId}/editar`);
      this.$emit('close');
    },
    
    listenToSoketi() {
      if (window.Echo) {
        window.Echo.private(`chat.patient.${this.patientId}`)
          .listen('.ChatMessageReceived', (e) => {
            const msg = e.chatMessage;
            // Só processa mensagens do paciente.
            // As do doutor ja entram pela action sendMessage, e so depois do
            // backend confirmar que sairam (salvia-810 — nada de otimismo aqui).
            if (msg && msg.sender === 'patient') {
              this.receiveMessage(msg);
              this.showNotification();
            } else if (msg && msg.sender === 'system') {
              // Resumo automático gerado em outra sessão aberta do mesmo chat.
              this.receiveMessage(msg);
              this.showSnack("Resumo automático da conversa gerado.", "info");
            }
          })
          .listen('.AnamneseAnswerReceived', (e) => {
            // Conforme o paciente responde, atualiza a aba da anamnese em tempo real.
            const anamneseId = e && e.anamnese_id;
            if (!anamneseId) return;
            const known = this.anamneses.some((a) => a.id === anamneseId);
            // Anamnese nova (primeira resposta da sessão): recarrega a lista de abas.
            if (!known) {
              this.fetchAnamneses(this.patientId);
            }
            // Recarrega as respostas se a aba já estiver carregada ou aberta.
            if (this.anamneseHistories[anamneseId] || this.activeTab === anamneseId) {
              this.fetchAnamneseHistories(anamneseId);
            } else {
              // Atualiza só o contador da aba quando ela não está aberta.
              this.fetchAnamneses(this.patientId);
            }
            this.showSnack("Nova resposta de anamnese recebida!", "success");
          });
      }
    },
    
    showNotification() {
      this.showSnack("Nova mensagem do paciente!", "success");
    },

    showSnack(text, color = "success") {
      this.snackbar = { show: true, text, color };
    },
    
    focusInput() {
      this.$nextTick(() => {
        this.$refs.inputField?.focus();
      });
    },
    
    adjustTextareaHeight() {
      const textarea = this.$refs.inputField;
      if (textarea) {
        textarea.style.height = "auto";
        textarea.style.height = Math.min(textarea.scrollHeight, 120) + "px";
      }
    },
    
    /**
     * Renderizada com v-html, e o texto vem do WhatsApp do paciente — ou seja,
     * é conteúdo de terceiro, gravado no banco pelo webhook sem tratamento.
     * Sem escapar, um `<img src=x onerror=...>` numa mensagem executava assim
     * que o médico abrisse a conversa (XSS armazenado).
     *
     * Mesmo padrão do safeMessage do GlobalConfirmDialog: escapa TUDO primeiro
     * e só então reintroduz a formatação que o chat suporta — quebra de linha e
     * **negrito**. Como a formatação é aplicada sobre a string já escapada, o
     * `$1` do negrito nunca carrega HTML vivo.
     */
    formatMessage(text) {
      if (!text) return "";
      return escapeHtml(text)
        .replace(/\n/g, "<br>")
        .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
    },

    /**
     * O anexo também chega pelo mesmo webhook e vai direto para um :href, onde
     * um `javascript:` executaria no clique. Só devolve a URL se o esquema for
     * navegável; o template esconde o link quando vier null.
     */
    safeMediaUrl(message) {
      return safeUrl(message?.media_url);
    },
    
    async startChat() {
      if (!this.initialMessage.trim() || !this.inputPhone) return;
      const result = await this.$store.dispatch("chat/startChat", {
        patientId: this.patientId,
        initialMessage: this.initialMessage,
        phone: this.inputPhone
      });
      if (!result || !result.success) {
        this.showSnack(
          (result && result.error) || "Falha ao iniciar a conversa.",
          "error"
        );
      }
      this.scrollToBottom();
    },

    async restartChat() {
      const phone = this.inputPhone || this.patientPhone;
      if (!phone) {
        this.showSnack("Paciente sem telefone cadastrado.", "error");
        return;
      }
      const ok = await this.$confirm({
        type: "warning",
        title: "Reiniciar conversa",
        message:
          "Isso reenvia o convite (template) pelo WhatsApp e reabre a janela de conversa de 24h. Deseja continuar?",
        confirmText: "Reiniciar"
      });
      if (!ok) return;

      const result = await this.$store.dispatch("chat/startChat", {
        patientId: this.patientId,
        initialMessage: this.initialMessage,
        phone
      });
      if (result && result.success) {
        this.showSnack(
          "Convite reenviado. A janela de conversa foi reaberta no WhatsApp.",
          "success"
        );
      } else {
        this.showSnack(
          (result && result.error) || "Falha ao reiniciar a conversa.",
          "error"
        );
      }
      this.scrollToBottom();
    },
    
    async sendMessage() {
      if (!this.newMessage.trim() || this.isTyping) return;

      const messageText = this.newMessage;
      this.newMessage = "";
      if (this.$refs.inputField) {
        this.$refs.inputField.style.height = "auto";
      }

      const result = await this.$store.dispatch("chat/sendMessage", {
        patientId: this.patientId,
        message: messageText
      });

      // salvia-810: se nao saiu, o medico precisa VER. Antes o erro morria num
      // console.error e a tela nao dizia nada — indistinguivel de enviado.
      // Devolvemos o texto ao campo pra ele nao perder o que escreveu e poder
      // tentar de novo.
      if (!result || !result.success) {
        this.newMessage = messageText;
        this.showSnack(
          (result && result.error) || "A mensagem NÃO foi enviada.",
          "error"
        );
      } else if (result.aviso) {
        // Enviou, mas ainda nao chegou: conversa fechada, convite a caminho.
        this.showSnack(result.aviso, "warning");
      }

      this.scrollToBottom();
    },
    
    // salvia-810: `queued`/`sent` NAO entram — ainda podem virar `delivered`.
    // So afirmamos falha no estado terminal, senao o alerta pisca a toa.
    // ESPELHA `ChatMessage::STATUS_FALHOS` (backend). Sao duas listas que TEM de
    // concordar, e elas divergiram: aqui so havia ["undelivered","failed"], os
    // dois estados do Twilio. Os estados NOSSOS ficaram de fora, e como
    // `recusada()` e `preterida()` sao testados DENTRO deste bloco, nenhum dos
    // dois avisos jamais apareceu — nem "o paciente pediu para nao conversar
    // agora" (salvia-810/823), nem a mensagem preterida (salvia-834).
    //
    // Se um estado novo entrar no STATUS_FALHOS do backend, tem de entrar aqui.
    naoEntregue(msg) {
      return (
        msg &&
        msg.sender === "doctor" &&
        [
          "undelivered",
          "failed",
          "recusado_pelo_paciente",
          "preterida_por_mensagem_nova",
        ].includes(msg.delivery_status)
      );
    },
    // 63016 = mensagem livre fora da janela de 24h. E o caso de longe mais
    // comum, e o unico em que o medico consegue resolver sozinho.
    foraDaJanela(msg) {
      return Number(msg?.delivery_error_code) === 63016;
    },
    // Recusa e diferente de falha tecnica: "tente de novo" seria o conselho
    // errado. O paciente decidiu, e o medico precisa ler isso, nao um erro.
    recusada(msg) {
      return msg?.delivery_status === "recusado_pelo_paciente";
    },
    // salvia-834: esta mensagem foi SUBSTITUIDA por outra antes de sair. A
    // comunicacao guarda uma mensagem represada so, e toda nova sobrescreve a
    // anterior — o paciente recebeu a ULTIMA. Nao e falha tecnica nem recusa: a
    // causa foi o proprio medico ter escrito de novo, e por isso o texto explica
    // isso em vez de mandar "tente de novo" sem contexto.
    preterida(msg) {
      return msg?.delivery_status === "preterida_por_mensagem_nova";
    },
    // Nao e falha: o convite foi enviado e a mensagem sai quando o paciente
    // responder. Distinguir isso de "nao entregue" importa — um pede acao do
    // medico, o outro pede so paciencia.
    aguardandoJanela(msg) {
      return msg?.delivery_status === "aguardando_janela";
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.messagesContainer;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
    }
  }
};
</script>

<style scoped>
.chat-container {
  display: flex;
  flex-direction: column;
  /* salvia-822: era `height: 100%` + `max-height: 85vh`.
   *
   * O pai (`.custom-drawer`, no PatientProfileDashboardPage) tem `height: auto`
   * com `min-height: 70vh`. Contra pai de altura AUTO, `height: 100%` nao tem o
   * que resolver e vira `auto` — entao o container ficava do tamanho do conteudo
   * e o `flex: 1` da area de mensagens nao tinha espaco para distribuir.
   *
   * Os dois sintomas do card saem dai: a area de conversa menor do que poderia
   * (o flex nao expandia) e a faixa vazia embaixo (o `min-height: 70vh` do
   * drawer sobrava abaixo do conteudo).
   *
   * Altura DEFINIDA resolve as duas de uma vez: o flex passa a ter espaco, e o
   * drawer passa a se ajustar ao chat em vez do contrario. 85vh e o mesmo teto
   * de antes, e cabe no `max-height: 90vh` do drawer. */
  height: 85vh;
  background: #f8fafc;
  position: relative;
  overflow: hidden;
}

/* Header */
.chat-header {
  padding: 16px 20px;
  background: linear-gradient(180deg, #ffffff 0%, #F4F8F5 100%);
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #E6EFEA;
  box-shadow: 0 2px 6px rgba(31, 77, 63, 0.04);
  z-index: 10;
}

.header-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.patient-avatar {
  background: linear-gradient(135deg, var(--ds-primary) 0%, var(--ds-primary-deep) 60%, #1F4D3F 100%);
  box-shadow: 0 4px 12px rgba(31, 77, 63, 0.28);
  border: 2px solid #fff;
}

.header-title {
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: #263238;
  letter-spacing: -0.2px;
}

.header-subtitle {
  font-size: 10px;
  color: #2e7d32;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
}

/* Tabs */
.chat-tabs {
  flex: 0 0 auto;
  border-bottom: 1px solid #E6EFEA;
  background: var(--ds-surface);
}

.chat-tab {
  text-transform: none;
  letter-spacing: 0;
  font-size: 12.5px;
  font-weight: 600;
}

/* Q&A (anamnese) */
.qa-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.question-bubble {
  background: #f1f5f9 !important;
  border: 1px solid #e2e8f0 !important;
}

.qa-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: var(--ds-primary);
  margin-bottom: 2px;
}

.answer-bubble {
  background: var(--ds-primary);
  color: #fff;
}

/* Messages Area */
.messages-wrapper {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  scroll-behavior: smooth;
}

.message-row {
  display: flex;
  width: 100%;
}

.user-row {
  justify-content: flex-end;
}

.bot-row {
  justify-content: flex-start;
}

.system-row {
  justify-content: center;
}

.message-bubble {
  max-width: 85%;
  padding: 12px 16px;
  border-radius: 18px;
  font-size: 14px;
  line-height: 1.5;
  position: relative;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.03);
}

.message-bubble.user {
  background: var(--ds-primary);
  color: white;
  border-bottom-right-radius: 4px;
}

.message-bubble.bot {
  background: var(--ds-surface);
  color: #334155;
  border-bottom-left-radius: 4px;
  border: 1px solid #E6EFEA;
}

.message-bubble.system {
  max-width: 92%;
  background: #fff8e1;
  color: #5d4037;
  border: 1px solid #ffe0b2;
  border-radius: 12px;
  text-align: left;
}

.system-label {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  margin-bottom: 6px;
  opacity: 0.85;
}

.message-bubble.system .system-label .v-icon {
  color: inherit;
}

.message-bubble.system .message-time {
  text-align: right;
}

.message-time {
  font-size: 10px;
  margin-top: 4px;
  opacity: 0.7;
}

.user .message-time {
  text-align: right;
}

/* Typing Animation */
.typing {
  display: flex;
  gap: 4px;
  padding: 12px 20px;
}

.dot {
  width: 6px;
  height: 6px;
  background: #cbd5e1;
  border-radius: 50%;
  animation: bounce 1.4s infinite ease-in-out both;
}

.dot:nth-child(1) { animation-delay: -0.32s; }
.dot:nth-child(2) { animation-delay: -0.16s; }

@keyframes bounce {
  0%, 80%, 100% { transform: scale(0); }
  40% { transform: scale(1.0); }
}

/* Empty State */
.empty-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: #94a3b8;
  padding-bottom: 40px;
}

.empty-state p {
  margin-top: 16px;
  font-weight: 500;
  margin-bottom: 24px;
}

.suggestion-chips {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: 100%;
}

/* Input Area */
.input-area {
  /* Nunca encolhe: sem isto o default `flex: 0 1 auto` deixa o campo espremer
   * quando o conteudo aperta, que e justamente o que se quer evitar aqui. */
  flex: 0 0 auto;
  padding: 20px;
  background: var(--ds-surface);
  border-top: 1px solid rgba(0, 0, 0, 0.05);
}

.input-wrapper {
  background: #f1f5f9;
  border-radius: 24px;
  padding: 8px 12px 8px 20px;
  display: flex;
  align-items: flex-end;
  gap: 8px;
  transition: box-shadow 0.2s, background 0.2s;
  border: 1px solid transparent;
}

.input-wrapper:focus-within {
  background: var(--ds-surface);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  border-color: rgba(31, 77, 63, 0.2);
}

textarea {
  flex: 1;
  border: none;
  background: transparent;
  outline: none;
  font-size: 14px;
  color: #1e293b;
  padding: 8px 0;
  resize: none;
  max-height: 120px;
  line-height: 1.4;
}

.send-btn {
  margin-bottom: 2px;
}

/* Scrollbar */
.messages-wrapper::-webkit-scrollbar {
  width: 6px;
}

.messages-wrapper::-webkit-scrollbar-track {
  background: transparent;
}

.messages-wrapper::-webkit-scrollbar-thumb {
  background: #e2e8f0;
  border-radius: 3px;
}

.messages-wrapper::-webkit-scrollbar-thumb:hover {
  background: #cbd5e1;
}

/* Aviso de falha de entrega: discreto, mas impossivel de nao ver — o medico
   precisa saber que o paciente NAO recebeu (salvia-810). */
.message-undelivered {
  margin-top: 4px;
  font-size: 0.72rem;
  line-height: 1.3;
  color: #c62828;
  font-weight: 500;
}

/* Espera, nao falha: âmbar em vez de vermelho. A mensagem vai chegar. */
.message-waiting {
  margin-top: 4px;
  font-size: 0.72rem;
  line-height: 1.3;
  color: #ef6c00;
  font-weight: 500;
}
</style>
